const cachedScripts = new Map();

type Append = 'head' | 'body';
type Attributes = Array<Partial<HTMLScriptElement>>;
type Preload = boolean | 'preload' | 'preloadmodule';
type LoadScriptOptions = {
  url: string;
  name?: string;
  // preload?: Preload;
  attributes?: Attributes;
  append?: Append;
};

const loadScriptDefaults: Partial<LoadScriptOptions> = {
  attributes: [{ async: true }],
  append: 'head'
};

const appendScript = (
  document: Document,
  script: HTMLScriptElement | HTMLLinkElement,
  append: Append
) => {
  if (document.currentScript && document.currentScript.parentNode) {
    document.currentScript.parentNode.insertBefore(
      script,
      document.currentScript
    );
  } else {
    (document[append] || document.getElementsByTagName(append)[0]).appendChild(
      script
    );
  }
};

export const createBrowserScript = (
  url: string,
  attributes: Attributes,
  append: Append
) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.onerror = () => reject(new Error(`Failed to load ${url}`));
    script.onload = resolve;
    (script as any).onreadystatechange = resolve;
    script.src = url;

    [...(loadScriptDefaults.attributes as any), ...(attributes || [])].forEach(
      attr => {
        const [attributeName, attributeValue] = Object.entries(attr)[0];

        (script as any)[attributeName] = attributeValue;
      }
    );

    appendScript(document, script, append);
  });

const loadScript = async (
  options: LoadScriptOptions,
  createScript = createBrowserScript,
  cache = true
) => {
  let promise: Promise<any>;
  const { url, name, attributes, append } = {
    ...loadScriptDefaults,
    ...options
  };

  // check to see if we have previously loaded this url and cached it
  if (cache && cachedScripts.has(url)) {
    promise = cachedScripts.get(url);
  } else {
    promise = Promise.all([
      createScript(url, attributes as any, append as any)
    ]);

    if (cache) cachedScripts.set(url, promise);
  }

  return promise
    .then(res => {
      if (!name) return null;

      if (global[name]) return global[name];

      if (res) return res;

      throw new Error(`${name} was not created by ${url}`);
    })
    .catch(err => {
      console.error('there was an error trying to load script', { err });
      return err;
    });
};

export default loadScript;
